import { yupResolver } from '@hookform/resolvers/yup';
import { useQueries } from '@tanstack/react-query';
import { OnboardingType, OpenEnrollmentPeriodDto } from '@zorro/clients';
import {
  UNEXPECTED_ERROR_MESSAGE,
  maximalDateISO,
  minimalDateISO,
} from '@zorro/common-utils';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import {
  formatDateISO,
  parseDate,
  parseDateISO,
} from '@zorro/shared/formatters';
import {
  Box,
  DateInput,
  DateRangeInput,
  Group,
} from '@zorro/web/design-system';
import {
  callEndpoint,
  showErrorNotification,
  useBatchCallEndpoint,
  useForm,
  useIdempotencyKey,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { FormFooter } from '../../FormFooter/FormFooter';
import { InformationBoxComponent } from '../../InformationBox';
import { RecordForActions } from '../EmployeeActionsUtils';
import { ErrorsTable } from '../Errors/ErrorsTable';
import {
  ChangeElectionWindowFormFields,
  ChangeElectionWindowSchemaObject,
  ChangeEnrollmentsFormFields,
  addOrEditAlertBoxText,
  formFieldStyle,
  getSharedEnrollmentType,
  handleFormStateChange,
  inlineGroupStyle,
} from './changeEnrollment.utils';

interface ChangeElectionWindowFormProps {
  planYearId: OpenEnrollmentPeriodDto['id'];
  recordsForActions: RecordForActions[];
  onSuccess?: () => void;
}

export const ChangeElectionWindowForm = ({
  recordsForActions,
  planYearId,
  onSuccess,
}: ChangeElectionWindowFormProps) => {
  'use no memo';
  const isSingleEmployee = recordsForActions.length === 1;
  const singleEmployee = recordsForActions[0];
  const idempotencyKey = useIdempotencyKey();

  const { data: openEnrollmentDto } = useMonolithQuery({
    method: 'openEnrollmentPeriodsControllerFind',
    params: [planYearId],
  });

  const { data: employer } = useMonolithQuery({
    method: 'employersControllerFindOne',
    params: openEnrollmentDto && [openEnrollmentDto.employerId],
  });

  const { data: associatedOnboardingPeriodsSingleEmployee } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindMany',
    params: [singleEmployee?.employeeId],
    enabled: isSingleEmployee,
  });

  const associatedOtherOnboardingPeriodsSingleEmployee =
    associatedOnboardingPeriodsSingleEmployee?.filter(
      (onboardingPeriod) =>
        onboardingPeriod.id !== singleEmployee.onboardingPeriodId
    );

  // 🧠 tech debt: the "change election window" needs to be written as a dedicated endpoint in the BE
  const selectedEmployeesOnboardingPeriodsQueries = useMemo(() => {
    const queries = [];

    for (const {
      employeeId,
      fullName,
      onboardingPeriodId,
    } of recordsForActions) {
      if (!onboardingPeriodId) {
        showErrorNotification({
          message: `${fullName} doesn't have an onboarding period`,
        });
        continue;
      }

      queries.push({
        queryKey: [
          'onboardingPeriodsControllerFindOne',
          [employeeId, onboardingPeriodId],
        ],
        queryFn: async () =>
          callEndpoint({
            method: 'onboardingPeriodsControllerFindOne',
            params: [employeeId, onboardingPeriodId],
          }),
      });
    }

    return queries;
  }, [recordsForActions]);

  const selectedEmployeesOnboardingPeriods = useQueries({
    queries: selectedEmployeesOnboardingPeriodsQueries,
  }).flatMap((query) => query.data ?? []);

  const singleEmployeeOnboardingPeriod = isSingleEmployee
    ? selectedEmployeesOnboardingPeriods[0]
    : undefined;

  const { data: singleEmployeeDto } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [singleEmployee?.employeeId],
    enabled: isSingleEmployee,
  });

  const {
    executeBatchCall: updateOnboardingPeriods,
    errors: updateOnboardingPeriodsErrors,
  } = useBatchCallEndpoint({
    methodName: 'onboardingPeriodsControllerUpdateByAdmin',
    singularItemName: 'Election window',
    action: 'updated',
  });

  const sharedEnrollmentType = getSharedEnrollmentType(
    selectedEmployeesOnboardingPeriods
  );

  const defaultValues: ChangeElectionWindowFormFields | undefined =
    isSingleEmployee && singleEmployeeOnboardingPeriod
      ? {
          electionWindow: [
            parseDateISO(
              singleEmployeeOnboardingPeriod.onboardingFrom
            ).toDate(),
            parseDateISO(
              singleEmployeeOnboardingPeriod.onboardingUntil
            ).toDate(),
          ],
          expectedStartOfCoverage: parseDateISO(
            singleEmployeeOnboardingPeriod.effectiveFrom
          ).toDate(),
        }
      : undefined;

  const { control, setValue, handleSubmit, formState, watch } =
    useForm<ChangeElectionWindowFormFields>({
      mode: 'all',
      resolver: yupResolver(ChangeElectionWindowSchemaObject),
      defaultValues,
    });

  const { isValid } = formState;

  const electionWindow = watch('electionWindow');

  const formValues = {
    planYear: openEnrollmentDto,
    enrollmentType: sharedEnrollmentType,
    electionWindow,
  };

  const onSubmit = handleSubmit(async (data) => {
    const { electionWindow, expectedStartOfCoverage } = data;
    const [electionFrom, electionUntil] = electionWindow || [];

    if (!electionFrom || !electionUntil || !expectedStartOfCoverage) {
      showErrorNotification({ message: UNEXPECTED_ERROR_MESSAGE });
      return;
    }

    const electionWindowOnlyData = {
      onboardingFrom: formatDateISO(electionFrom),
      onboardingUntil: formatDateISO(electionUntil),
      expectedStartOfCoverage: formatDateISO(expectedStartOfCoverage),
    };

    await updateOnboardingPeriods(
      recordsForActions.map(({ fullName, onboardingPeriodId = '' }) => {
        return {
          key: fullName,
          params: [
            onboardingPeriodId,
            idempotencyKey.getFor(onboardingPeriodId),
            electionWindowOnlyData,
          ],
        };
      }),
      {
        onSuccess,
      }
    );
  });

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Submit"
        primaryButtonProps={{
          onClick: onSubmit,
          disabled: !isValid,
        }}
      />
    ),
    [onSubmit, isValid]
  );
  useDrawerFooter(drawerFooter);

  return (
    <form onSubmit={onSubmit}>
      <Group>
        <InformationBoxComponent
          title={addOrEditAlertBoxText(isSingleEmployee)}
        />
        <Box style={{ width: '100%' }}>
          <Box style={inlineGroupStyle}>
            <Controller
              control={control}
              name="electionWindow"
              render={({ field: { onChange, ...rest } }) => (
                <DateRangeInput
                  {...rest}
                  required
                  label="Election window"
                  maxDate={
                    sharedEnrollmentType === OnboardingType.OPEN_ENROLLMENT &&
                    openEnrollmentDto?.effectiveFrom
                      ? parseDateISO(openEnrollmentDto.effectiveFrom)
                          .subtract(1, 'day')
                          .toDate()
                      : undefined
                  }
                  onChange={(value) => {
                    const [newFrom, newUntil] = value;
                    if (newFrom && newUntil) {
                      formValues.electionWindow = [newFrom, newUntil];
                      handleFormStateChange(
                        employer?.monthlySEPDeadline ?? null,
                        employer?.isMonthlySEPDeadlineEndOfMonth ?? null,
                        formValues,
                        'electionWindow',
                        setValue as unknown as UseFormReturn<ChangeEnrollmentsFormFields>['setValue'],
                        singleEmployeeDto,
                        associatedOtherOnboardingPeriodsSingleEmployee
                      );
                    }
                    onChange(value);
                  }}
                  style={formFieldStyle}
                  disabled={!openEnrollmentDto}
                />
              )}
            />
            <Controller
              control={control}
              name="expectedStartOfCoverage"
              render={({ field }) => (
                <DateInput
                  {...field}
                  required
                  label="Expected start of coverage"
                  style={formFieldStyle}
                  minDate={
                    isSingleEmployee
                      ? sharedEnrollmentType === OnboardingType.SPECIAL &&
                        openEnrollmentDto?.effectiveFrom &&
                        electionWindow?.length
                        ? parseDateISO(
                            maximalDateISO(
                              formatDateISO(
                                parseDate(electionWindow?.[1]).add(1, 'day')
                              ),
                              openEnrollmentDto?.effectiveFrom,
                              singleEmployeeDto?.eligibleFrom
                            )
                          ).toDate()
                        : undefined
                      : electionWindow?.[1]
                        ? parseDateISO(
                            maximalDateISO(
                              formatDateISO(parseDate(electionWindow[1])),
                              openEnrollmentDto?.effectiveFrom
                            )
                          ).toDate()
                        : undefined
                  }
                  maxDate={
                    openEnrollmentDto?.effectiveUntil
                      ? sharedEnrollmentType === OnboardingType.SPECIAL
                        ? parseDateISO(
                            minimalDateISO(
                              openEnrollmentDto.effectiveUntil,
                              singleEmployeeDto?.eligibleUntil
                            )
                          ).toDate()
                        : parseDateISO(
                            openEnrollmentDto.effectiveUntil
                          ).toDate()
                      : undefined
                  }
                  disabled={!openEnrollmentDto}
                  isReadOnly={
                    sharedEnrollmentType === OnboardingType.OPEN_ENROLLMENT
                  }
                />
              )}
            />
          </Box>
        </Box>
      </Group>

      <ErrorsTable
        tableTitle="Employee name"
        errors={updateOnboardingPeriodsErrors}
        isBulk={recordsForActions.length > 1}
      />
    </form>
  );
};
